import type { JSX } from 'react';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { usePeut } from '../../auth/usePeut.js';
import { listerGaleries } from '../../lib/api.js';
import type { ComptabiliteAssocie, ComptabiliteDirection } from '../../types.js';
import { useFormats } from '../VueDAigle/formats.js';
import styles from '../VueDAigle/VueDAigle.module.css';
import { SERVICES_COMPTABLES } from '@artosera/shared';
import { Cloture } from './Cloture.js';
import {
  barresAssocie,
  barresDirection,
  fluxAssocie,
  fluxDirection,
  ordreBeneficiaires,
} from './donnees.js';
import { Encaissements } from './Encaissements.js';
import { Flux } from './Flux.js';
import { Grille } from './Grille.js';
import { Historique } from './Historique.js';
import { MoisEnChiffres } from './MoisEnChiffres.js';
import page from './Comptabilite.module.css';
import { libellesTypes, TypesRecetteContexte } from './nomsTypes.js';
import { useBrouillon } from './useBrouillon.js';
import { useDonneesGrille, useLectureComptabilite, useTypesRecette } from './useComptabilite.js';

/**
 * L'écran Comptabilité de la vue d'aigle (ARTO-58), direction « Flux » : le mois
 * en chiffres, le flux du HT des services vers les parties, douze mois en
 * barres, puis — pour la direction seule — les encaissements, les
 * pourcentages par type de recette (et la gestion des types) et la clôture. L'associé reçoit une autre réponse (`vue: 'associe'`) : ses tuiles,
 * sa part, un flux réduit à « votre part / reste du HT », ni TVA, ni grille, ni
 * clôture. L'API filtre ; l'écran rend ce qu'il reçoit.
 *
 * Le mois affiché vit dans l'adresse (`?mois=2026-09`) : on peut le partager.
 */
export function Comptabilite(): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const peut = usePeut();
  const [parametres, setParametres] = useSearchParams();
  const lecture = useLectureComptabilite();
  const donnees = lecture.data;
  const direction: ComptabiliteDirection | undefined =
    donnees?.vue === 'direction' ? donnees : undefined;
  const associe: ComptabiliteAssocie | undefined = donnees?.vue === 'associe' ? donnees : undefined;

  // La grille et la clôture sont la tâche de la direction : droit de la matrice ET vue complète.
  const gerer = direction !== undefined && peut('console:manage-accounting');
  const { versions, beneficiaires } = useDonneesGrille(gerer);
  const listeVersions = useMemo(() => versions.data ?? [], [versions.data]);
  const listeBeneficiaires = useMemo(() => beneficiaires.data ?? [], [beneficiaires.data]);
  // Le parc : nommer et choisir les galeries exclues (direction seule).
  const galeries = useQuery({
    queryKey: ['console', 'galeries'],
    queryFn: listerGaleries,
    enabled: gerer,
  });
  // Les types de recette : les cinq services, puis ceux que la direction a créés.
  const typesRecette = useTypesRecette(gerer);
  const listeTypes = useMemo(() => typesRecette.data ?? [], [typesRecette.data]);
  // Une ligne de grille par service, par type actif, et par type archivé qui a
  // encore une grille ou qu'une recette d'un mois OUVERT cite : un mois ouvert
  // qu'il a servi doit pouvoir se clôturer, et un type archivé avant d'avoir
  // eu sa grille doit pouvoir la recevoir pour régulariser ce mois.
  const lignesGrille = useMemo(() => {
    if (listeTypes.length === 0) return [...SERVICES_COMPTABLES];
    const citesOuverts = new Set(
      (direction?.mois ?? [])
        .filter((m) => m.statut === 'provisoire')
        .flatMap((m) => m.services.filter((s) => s.lignes > 0).map((s) => s.service)),
    );
    return listeTypes
      .filter(
        (type) =>
          type.base ||
          type.actif ||
          listeVersions.some((v) => v.service === type.id) ||
          citesOuverts.has(type.id),
      )
      .map((type) => type.id);
  }, [listeTypes, listeVersions, direction]);
  const brouillon = useBrouillon({
    donnees: gerer ? direction : undefined,
    versions: listeVersions,
    beneficiaires: listeBeneficiaires,
    types: lignesGrille,
  });
  // Le nom des types créés : ceux que la lecture cite, et la liste de la direction.
  const nomsTypes = useMemo(
    () => libellesTypes(donnees?.typesRecette ?? [], listeTypes),
    [donnees?.typesRecette, listeTypes],
  );

  // Les sections 01 à 03 montrent le brouillon appliqué ; la clôture, elle, lit le serveur.
  const affiche: ComptabiliteDirection | ComptabiliteAssocie | undefined =
    gerer && brouillon.apercu ? brouillon.apercu : donnees;

  const demande = parametres.get('mois');
  const mois =
    affiche !== undefined ? (affiche.mois.find((m) => m.mois === demande)?.mois ?? affiche.a) : '';
  const choisirMois = (valeur: string): void => {
    setParametres(
      (precedents) => {
        const suivants = new URLSearchParams(precedents);
        if (valeur === affiche?.a) suivants.delete('mois');
        else suivants.set('mois', valeur);
        return suivants;
      },
      { replace: true },
    );
  };

  // Le rang d'un bénéficiaire fixe sa couleur : le même ordre que la grille, lu du serveur.
  const ordre = useMemo(
    () =>
      direction
        ? ordreBeneficiaires(
            direction,
            listeBeneficiaires.map((b) => b.id),
          )
        : [],
    [direction, listeBeneficiaires],
  );

  return (
    <TypesRecetteContexte.Provider value={nomsTypes}>
      <div className={page.racine} aria-busy={lecture.isFetching || undefined}>
        <div className={styles.entete}>
          <div>
            <h1>{t('comptabilite.titre')}</h1>
            <p>
              {affiche
                ? t(direction ? 'comptabilite.texteDirection' : 'comptabilite.texteAssocie', {
                    date: f.jourMoisAnnee(affiche.genereeLe),
                  })
                : null}
            </p>
          </div>
        </div>

        {lecture.isError ? (
          <p className={styles.etat} role="alert">
            {t('comptabilite.erreur')}
          </p>
        ) : affiche === undefined ? (
          <p className={styles.etat} role="status">
            {t('comptabilite.chargement')}
          </p>
        ) : (
          <>
            {gerer && brouillon.servicesModifies.length > 0 ? (
              <div className={page.apercuBandeau} role="status">
                <span>
                  <strong>{t('comptabilite.apercu.titre')}</strong>{' '}
                  {t('comptabilite.apercu.texte', {
                    count: brouillon.servicesModifies.length,
                    mois: f.moisLong(brouillon.depuis),
                  })}
                </span>
                <a href="#pourcentages">{t('comptabilite.apercu.voir')}</a>
              </div>
            ) : null}

            <MoisEnChiffres vue={affiche} selection={mois} onChoisir={choisirMois} numero="01" />

            <div className={page.deuxColonnes}>
              <Flux
                associe={associe !== undefined}
                mois={mois}
                numero="02"
                schema={
                  affiche.vue === 'direction'
                    ? fluxDirection(
                        affiche.mois.find((m) => m.mois === mois)!,
                        ordre,
                      )
                    : fluxAssocie(affiche.mois.find((m) => m.mois === mois)!)
                }
              />
              <Historique
                associe={associe !== undefined}
                barres={
                  affiche.vue === 'direction'
                    ? barresDirection(affiche, ordre)
                    : barresAssocie(affiche)
                }
                selection={mois}
                onChoisir={choisirMois}
                numero="03"
              />
            </div>

            {gerer && direction ? (
              <>
                <Encaissements
                  key={`encaissements-${mois}`}
                  mois={mois}
                  moisClotures={
                    new Set(direction.mois.filter((m) => m.statut === 'cloture').map((m) => m.mois))
                  }
                  types={listeTypes}
                  versions={listeVersions}
                  beneficiaires={listeBeneficiaires}
                  galeries={galeries.data ?? []}
                  numero="04"
                />
                <Grille
                  brouillon={brouillon}
                  versions={listeVersions}
                  moisCourant={direction.a}
                  numero="05"
                  beneficiaires={listeBeneficiaires}
                  galeries={galeries.data ?? []}
                  lignes={lignesGrille}
                  types={listeTypes}
                />
                <Cloture
                  key={mois}
                  mois={direction.mois.find((m) => m.mois === mois)!}
                  moisCourant={direction.a}
                  brouillonEnCours={brouillon.servicesModifies.length > 0}
                  numero="06"
                />
              </>
            ) : null}
          </>
        )}
      </div>
    </TypesRecetteContexte.Provider>
  );
}
